import { ReactElement } from "react";
import { ApiURL } from "./Actions";
import {
  APP_STATUS,
  APP_ROLES,
  CREDENTIAL_MODAL_STATUS,
  RATING_SCORE_CODE,
  RATING_LEVEL_CODE,
  WHITELABEL
} from "./Constants";

type CssPropType = {
  style: React.CSSProperties;
};

export type IconComponentTypes = (props?: CssPropType) => JSX.Element;

export type StepDetail = {
  key: string;
  content: ReactElement;
};

export type AuthDataType = {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  expireTime: string;
  roles: string[];
  username: string;
  attributes?: any;
};

export type AppPathProps = {
  path: string;
};

export type SchoolContactTableType = {
  roleId?: string;
  schoolName?: string;
  school?: {
    schoolName: string;
  };
  email: string;
  title: string;
  givenName1: string;
  givenName2?: string;
  familyName: string;
  position: string;
  address1?: string;
  address2?: string;
  address3?: string;
  address4?: string;
  state?: string;
  auPostCode?: string;
  phone?: string;
  mobile?: string;
};

export type ApplicantType = {
  refNum: number;
  title: string;
  phone?: string;
  mobile?: string;
  gender: string;
  givenName1: string;
  givenName2?: string;
  familyName: string;
  email: string;
  dob: string;
  permanentAddress: {
    address1: string;
    address2: string;
    address3: string;
    postcode: string;
  };
  postalAddress: {
    address1: string;
    address2: string;
    address3: string;
    postcode: string;
  };
  applicationMode: string; //e.g. ALL-PORTAL-TA01 or PG-PORTAL something like that
  dateReceived: string;
  srsLastPage: number; //100, 200, 300, or 400
  applicantStatus?: string; // "L" or "P"
  verified?: boolean;
  eligibleForSrs?: boolean;
  srsLodged?: boolean;
};

export type PropertiesType = {
  maintenanceEndTime: string;
  maintenanceStartTime: string;
  ratingOpenDate: string;
  ratingCloseDate: string;
  // only in uac
  "ratingOpenDate.os"?: string;
  "ratingCloseDate.os"?: string;
  easOpenDate: string;
  easCloseDate: string;
  applicationOpenDate: string;
  applicationCloseDate: string;
  assessmentYear: string;
  applyHyperlink: string;
  keyDateHyperlink: string;
  staffCountMax: string;
};

export type ClosingDataType = {
  isSrsOpen: boolean;
  srsClosingDate: string;
  isEasOpen: boolean;
  easClosingDate: string;
  // uac only
  isSrsOpenOS: boolean;
  srsClosingDateOS: string;
};

export type WhiteLabelType = WHITELABEL | "";

export type SchoolReportType = {
  reportDays: string[];
};

export type DocumentUploadType = {
  error: boolean;
  docs?: any;
  errorInfo?: any;
};

export type StudentQualType = {
  refNum: number;
  number: number;
  level: string;
  origin: string;
  subOrigin: string;
  yearTo: number;
  schoolName: string;
  studentNumber: string;
  resultCode: string;
  curriculumState: string;
  examDate: string;
  currentlyEnrolled: boolean;
  scheduleCode: string;
  baseScheduleRank: number;
  brsOrder: number;
  ascendingLevelCodeShort: number;
  ascendingLevelCodeFull: number;
  docsProvided: boolean;
  archiveSearch: boolean;
  docsTaskNumber: number;
  docsMessageNumber: number;
  pendingTaskNumber: number;
  pendingMessageNumber: number;
  dateVerified: string;
  dateAssessed: string;
  qualificationSource: string;
  dateEntered: string;
  dateViewed: string;
};

export type SRSApplicantType = {
  studentNumber?: string;
  refNum: number;
  givenName1: string;
  givenName2?: string;
  fullName?: string;
  familyName: string;
  dob: string;
  levelOfQual: string;
  schoolCode: string;
  schoolName: string;
  schoolState: string;
  schoolRated: string;
};

export type SRSApplicantsState = {
  isLoading: boolean;
  isError: boolean;
  origin: SRSApplicantType[];
  tableData: SRSApplicantType[];
  paginatedTableData: SRSApplicantType[];
  currentPage: number;
  sorts: any;
  filters: any;
  search: string;
  searchOrigin: string;
  // Dashboard metrics
  statistics: {
    totalApplicants: number;
    ratedApplicants: number;
    unratedApplicants: number;
    partiallyRatedApplicants: number;
  };
};

export type StringIndexType = {
  [index: string]: string;
};

export type RatingScoresType = {
  qualNum?: number;
  score: RatingScoreCodeType;
  levelOfQual: RatingLevelCodeType;
};

export type RatingTopicsType = {
  elementId: string;
  ratingGroupId: number;
  displayOrder: number;
  topic: string;
};

export type RatingScoreBodyType = {
  ratings: {
    refNum: number;
    draft: boolean;
    qualRatings: QualRatingsType[];
  };
};

export type CommentType = {
  comment: string;
  commentType: string;
  dateEntered: string;
  seqNum: number;
};
export type DocumentType = string;

type QualRatingsType = {
  levelOfQual: string;
  rating: number;
  qualNum: number;
};

export type RatingQualsType = {
  RSTL?: RatingScoreCodeType;
  RSTN?: RatingScoreCodeType;
  RSTT?: RatingScoreCodeType;
  RSTM?: RatingScoreCodeType;
  RSTC?: RatingScoreCodeType;
  RSTI?: RatingScoreCodeType;
  RSTE?: RatingScoreCodeType;
  comment?: string;
};

type AppRolesKeys = keyof typeof APP_ROLES;
export type AppRolesValuesType = (typeof APP_ROLES)[AppRolesKeys];

type ApiURLKeys = keyof typeof ApiURL;
export type ApiURLType = (typeof ApiURL)[ApiURLKeys];

type CredentialModalStatusKeys = keyof typeof CREDENTIAL_MODAL_STATUS;
export type CredentialModalStatusType = (typeof CREDENTIAL_MODAL_STATUS)[CredentialModalStatusKeys];

type AppStatusKeys = keyof typeof APP_STATUS;
export type AppStatusType = (typeof APP_STATUS)[AppStatusKeys];

type RatingLevelCodeKeys = keyof typeof RATING_LEVEL_CODE;
export type RatingLevelCodeType = (typeof RATING_LEVEL_CODE)[RatingLevelCodeKeys];

type RatingScoreCodeKeys = keyof typeof RATING_SCORE_CODE;
export type RatingScoreCodeType = (typeof RATING_SCORE_CODE)[RatingScoreCodeKeys];

export type StaffUserType = {
  email: string;
  firstName: string;
  lastName: string;
  roles: AppRolesValuesType[];
  position: string;
  address1?: string;
  address2?: string;
  address3?: string;
  town?: string;
  seqNum: number;
  passwordSet: boolean;
};

export enum TokenResultCode {
  VALID = "VALID",
  EXPIRED = "EXPIRED",
  INVALID = "INVALID",
  ALREADY_EXISTS = "ALREADY_EXISTS",
  MISSING = "MISSING"
}

export type PaginationPropType = {
  defaultCurrent: number;
  total: number;
  pageSize: number;
  current: number;
  responsive: boolean;
  showTotal: any;
};

export interface ILogin {
  [key: string]: any;
}

export type TRoleOption<T> = {
  [key: string]: T;
};

export type RoleType = {
  value: string;
  labelKey: string;
  disabled?: boolean;
};

export interface TableTitleProps {
  sortOrder?: string;
  iconColor?: "black" | "grey" | "white";
  title: string;
  altText: string;
  tooltipTitle: string;
}

export enum Env {
  "DOMESTIC" = "ug",
  "INTERNATIONAL" = "os"
}

export type ApiUgOsQueryParams = { source: Env | null };
